import React from 'react';

import { Aedu } from 'global';

import { isCoronavirusWork } from 'js/coronavirus/coronavirus_research_detector';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';

import {
  BulkDownloadableWork,
  bulkDownloadDownloadSource,
  bulkDownloadImpressionSource,
  BulkDownloadTrigger,
  giveGatedDownloadOnUpsellClose,
  externalFallbackUrl,
  navigateToCheckout,
  updateBulkDownloadLookback,
} from './bulk_download_helper';
import renderModal from 'js/utils/renderModal';
import { markModalOpening } from 'js/utils/modalPresence';
import { fetchFeatureEnabled } from 'js/research_radar/api/researchRadarApi';
import { loadWonderchatScript } from './wonderchat_widget';
import { ModalContext } from 'js/bulk_download/autotest/AutotestBulkDownload';
import { abTest as CiterightMarketingDownloadAbTest } from 'js/ab_tests/tests/citeright_2026_04_marketing_modal_download_flow';

type Options = {
  trigger: BulkDownloadTrigger;
  work: BulkDownloadableWork;
  // `gatedDownload` indicates whether the single-work download should be gated
  // behind the upsell (i.e., user gets the download when the modal closes).
  gatedDownload?: boolean;
  autoStartBulkDownload?: boolean;
  purchased?: boolean;
  readerMessageLimitReached?: boolean;
  setReaderMessageLimitReached?: (value: boolean) => void;
  /** Whether the user passes the synchronous CiteRight encore-email
   * eligibility filter on this work (post_download_data API). Sourced
   * server-side from Treatments::CiterightDownloadInvitation
   * .eligible_at_download_time?. Used to gate AB test bucketing for the
   * CITE-232 marketing modal. */
  citerightMarketingModalEligible?: boolean;
  /** External publisher landing page URL. When present (system-owned works whose
   * license forbids storing the attachment), the progress bar view shows a
   * "Download via publisher" link instead of relying on a stored attachment. */
  landingPageUrl?: string;
};

export async function showBulkDownloadModal({
  trigger,
  work,
  gatedDownload,
  autoStartBulkDownload,
  purchased,
  readerMessageLimitReached = true,
  setReaderMessageLimitReached = () => {},
  citerightMarketingModalEligible = false,
  landingPageUrl,
}: Options): Promise<void> {
  const isPremium: boolean = Aedu.User?.current?.premium;
  const coronavirusWork = isCoronavirusWork(work);
  const shouldStartWorkDownloadOnUpsellClose = gatedDownload === true;

  // Mark a modal as opening SYNCHRONOUSLY, before the async import below, so a
  // deferred launcher (the full screen premium modal, shown after
  // lcpFinalized()) that runs in a microtask after this same click sees that a
  // modal is on its way and skips, instead of stacking on top. Released when the
  // modal closes, or on import failure so the flag can't get stuck.
  const releaseModalPresence = markModalOpening();

  if (isPremium || coronavirusWork || purchased) {
    const radarEnabledPromise = isPremium
      ? fetchFeatureEnabled()
      : Promise.resolve(false);

    Promise.all([
      import(
        './WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal'
      ),
      radarEnabledPromise,
    ])
      .then(([module, radarEnabled]) => {
        const PremiumModal = module.default;
        renderModal(
          (onClose) => (
            <PremiumModal
              onClose={onClose}
              sourceWork={{
                id: work.id,
                title: work.title,
                slug: work.slug,
                doi: work.doi,
                hasDownloadableAttachment: !!work.attachment.id,
              }}
              downloadImmediately={autoStartBulkDownload}
              researchRadarEnabled={radarEnabled}
              landingPageUrl={landingPageUrl}
            />
          ),
          { afterClose: releaseModalPresence },
        );

        if (isPremium && trigger.startsWith('swp-')) {
          loadWonderchatScript();
        }
      })
      .catch(() => releaseModalPresence());
  } else {
    const afterFirstModalClose = () => {
      if (shouldStartWorkDownloadOnUpsellClose) {
        maybeDownloadWork(
          trigger,
          work,
          readerMessageLimitReached,
          setReaderMessageLimitReached,
          citerightMarketingModalEligible,
        );
      }
      updateBulkDownloadLookback(trigger);
    };

    const ctx: ModalContext = { trigger };

    // When the user clicks the CTA, the modal snapshots the current filter
    // state and work IDs into sessionStorage (see
    // bulk_download_checkout_state.ts). After checkout, the premium modal
    // consumes that snapshot and uses the saved IDs for the auto-download so
    // the user gets exactly what they saw before upgrading. Storage is keyed
    // by the source work ID, has a 30-min TTL, and is cleared on dismiss.
    const landingPageHostname = landingPageUrl
      ? (() => {
          try {
            return new URL(landingPageUrl).hostname;
          } catch {
            return undefined;
          }
        })()
      : undefined;
    // For works where licenseAllowsAttachment is false, we cannot serve the
    // PDF ourselves — the paper must be fetched from the publisher's site.
    // In that case, the upsell secondary action should direct the user to
    // the paper externally (via landing page, DOI resolver, or Scholar)
    // rather than showing a plain "No thanks" that leaves them with nothing.
    //
    // This condition is intentionally strict: user-owned works and system-owned
    // works whose license permits hosting the attachment both get the default
    // "No thanks, I'll stay on the free tier" dismiss, even if they have a DOI.
    const attachmentExternalOnly = work.licenseAllowsAttachment === false;
    const fallbackUrl =
      !landingPageHostname && attachmentExternalOnly
        ? externalFallbackUrl(work.doi, work.title)
        : undefined;
    const externalUrl = landingPageHostname ? landingPageUrl : fallbackUrl;
    const secondaryActionLabel = landingPageHostname
      ? `No thanks, continue to ${landingPageHostname}`
      : work.doi && attachmentExternalOnly
        ? 'No thanks, view via DOI'
        : fallbackUrl
          ? 'No thanks, search Google Scholar'
          : undefined;

    const sharedProps = {
      onPrimaryCtaClicked: () =>
        navigateToCheckout(trigger, work.id, new Set()),
      impressionSource: bulkDownloadImpressionSource('upsell', trigger),
      sourceWork: work,
      secondaryActionLabel,
      onSecondaryAction: externalUrl
        ? () => window.open(externalUrl, '_blank', 'noopener,noreferrer')
        : undefined,
      hideIncludedWorkValueProp: !!landingPageUrl,
    };

    import('./WorkCardsBulkDownloadUpsellModal')
      .then((upsellModule) => {
        const UpsellModal = upsellModule.default;
        renderModal(
          (onClose) => (
            <UpsellModal onClose={onClose} ctx={ctx} {...sharedProps} />
          ),
          {
            afterClose: () => {
              releaseModalPresence();
              afterFirstModalClose();
            },
          },
        );
      })
      .catch(() => releaseModalPresence());
  }
}

function maybeDownloadWork(
  trigger: BulkDownloadTrigger,
  work: BulkDownloadableWork,
  readerMessageLimitReached: boolean,
  setReaderMessageLimitReached: (value: boolean) => void,
  citerightMarketingModalEligible: boolean,
): void {
  if (!giveGatedDownloadOnUpsellClose(trigger)) return;
  // Skip if there is no stored attachment (e.g. system-owned works with an
  // external landing page URL).  Clicking an empty href would navigate to the
  // current page rather than downloading a file.
  if (!work.attachment.url) return;
  const link = document.createElement('a');
  link.href = work.attachment.url;
  link.setAttribute('target', '_blank');
  link.setAttribute('style', 'display: none');
  document.body.appendChild(link);
  link.click();

  // CITE-232 AB test: in the variant arm, replace the post-download
  // "Send a note" modal with a CiteRight marketing bridge modal. Bucket only
  // when the user passes the encore-email eligibility filter so the cohort
  // matches the population that would otherwise have received an encore.
  // Encore email continues to send unchanged in both arms.
  if (citerightMarketingModalEligible) {
    const bucket = CiterightMarketingDownloadAbTest();
    if (bucket === 'variant') {
      // Mark presence synchronously so the queued FSP modal waits for this
      // post-download modal too (released when it closes / on import failure).
      const releaseCiteright = markModalOpening();
      import('js/liswp/modals_ds/CiterightMarketingModal')
        .then((module) => {
          const { CiterightMarketingModal } = module;
          renderModal(
            (onClose) => (
              <CiterightMarketingModal onClose={onClose} workId={work.id} />
            ),
            { afterClose: releaseCiteright },
          );
        })
        .catch(() => releaseCiteright());
      return;
    }
  }

  // Skip reader message modal if no authors (system-owned works)
  const primaryAuthor = work.authors[0];
  if (readerMessageLimitReached || !primaryAuthor) return;

  // Mark presence synchronously so the queued FSP modal waits for the reader
  // message modal too (released when it closes / on import failure).
  const releaseReaderMessage = markModalOpening();
  import('js/liswp/modals_ds/ReaderMessageModal')
    .then((module) => {
      const { ReaderMessageModal } = module;
      renderModal(
        (onClose) => (
          <ToastProvider>
            <ReaderMessageContext.Provider
              value={{
                readerMessageLimitReached,
                setReaderMessageLimitReached,
                citerightMarketingModalEligible,
                setCiterightMarketingModalEligible: () => {},
              }}
            >
              <ReaderMessageModal
                onClose={onClose}
                primaryAuthor={{
                  id: primaryAuthor.id,
                  displayName: primaryAuthor.displayName,
                  photo: primaryAuthor.photo,
                }}
                workId={work.id}
                attachmentId={work.attachment.id}
                readerMessageSource={bulkDownloadDownloadSource(trigger)}
              />
            </ReaderMessageContext.Provider>
          </ToastProvider>
        ),
        { afterClose: releaseReaderMessage },
      );
    })
    .catch(() => releaseReaderMessage());
}
